import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { usePageContent, getSection } from "@/hooks/usePageContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SEO from "@/components/SEO";
import EventRegistrationForm from "@/components/EventRegistrationForm";
import { toast } from "sonner";
import { 
  ArrowRight, 
  Calendar,
  Clock,
  MapPin,
  Users,
  Video,
  Globe,
  Mic,
  BookOpen,
  Filter,
  ChevronRight,
  Bell,
  Sparkles,
  Loader2,
  ExternalLink
} from "lucide-react";

const API_URL = window.location.origin;

// Static fallback events data
const staticEvents = [
  // Upcoming Events
  {
    id: 1,
    title: 'USA University Fair 2026',
    type: 'expo',
    description: 'Meet representatives from 50+ top US universities. Get on-the-spot profile evaluations and application fee waivers.',
    date: '2026-03-15',
    time: '10:00 AM - 6:00 PM',
    location: 'The Leela Palace, New Delhi',
    mode: 'in-person',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800',
    universities: ['MIT', 'Stanford', 'Columbia', 'NYU', 'USC'],
    max_attendees: 500,
    registration_link: 'https://forms.google.com/example',
    isFeatured: true,
    tags: ['USA', 'Multiple Universities', 'Fee Waiver']
  },
  {
    id: 2,
    title: 'IELTS Masterclass: Band 8+ Strategies',
    type: 'webinar',
    description: 'Learn proven strategies to score Band 8+ in IELTS from our expert trainers with 10+ years of experience.',
    date: '2026-02-10',
    time: '7:00 PM - 9:00 PM',
    location: 'Online (Zoom)',
    mode: 'online',
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800',
    speaker: 'Dr. Anita Sharma',
    speakerRole: 'IELTS Expert, British Council Certified',
    max_attendees: 200,
    isFeatured: false,
    tags: ['IELTS', 'Test Prep', 'Free']
  },
  {
    id: 3,
    title: 'Canada PR Pathway Workshop',
    type: 'workshop',
    description: 'Complete guide to Canadian PR through study pathway. Learn about Express Entry, PNP, and post-study work permits.',
    date: '2026-02-20',
    time: '11:00 AM - 2:00 PM',
    location: 'UniXperts Office, Gurugram',
    mode: 'hybrid',
    image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?w=800',
    speaker: 'Rajesh Kumar',
    speakerRole: 'Immigration Consultant, ICCRC Member',
    max_attendees: 50,
    registration_link: 'https://eventbrite.com/example',
    isFeatured: true,
    tags: ['Canada', 'Immigration', 'PR Pathway']
  },
  {
    id: 4,
    title: 'UK Scholarship Opportunities 2026',
    type: 'webinar',
    description: 'Discover scholarship opportunities worth £50,000+ for Indian students. Chevening, Commonwealth, and university-specific scholarships.',
    date: '2026-02-25',
    time: '6:00 PM - 7:30 PM',
    location: 'Online (Google Meet)',
    mode: 'online',
    image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800',
    speaker: 'British Council Representative',
    speakerRole: 'Education UK',
    max_attendees: 300,
    isFeatured: false,
    tags: ['UK', 'Scholarships', 'Free']
  },
  {
    id: 5,
    title: 'Germany Study Guide: Zero Tuition Universities',
    type: 'webinar',
    description: 'Learn how to study in Germany with almost zero tuition fees. Blocked account, language requirements, and application process.',
    date: '2026-03-01',
    time: '5:00 PM - 6:30 PM',
    location: 'Online (Zoom)',
    mode: 'online',
    image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800',
    speaker: 'DAAD Representative',
    max_attendees: 250,
    isFeatured: false,
    tags: ['Germany', 'Tuition Free', 'Free']
  },
  {
    id: 6,
    title: 'Australia Education Expo',
    type: 'expo',
    description: 'Meet admissions officers from Group of Eight universities and explore scholarship opportunities.',
    date: '2026-03-10',
    time: '10:00 AM - 5:00 PM',
    location: 'ITC Maratha, Mumbai',
    mode: 'in-person',
    image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800',
    universities: ['Melbourne', 'Sydney', 'ANU', 'UNSW', 'Monash'],
    max_attendees: 400,
    registration_link: 'https://unixperts.com/register/australia-expo',
    isFeatured: true,
    tags: ['Australia', 'Group of Eight', 'Scholarships']
  }
];

// Past Events
const staticPastEvents = [
  {
    id: 101,
    title: 'MBA Admissions Bootcamp',
    event_type: 'workshop',
    event_date: '2024-12-15',
    attendees: 150,
    featured_image: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800',
    highlights: ['20+ admits to top B-schools', 'Live SOP reviews', 'Mock interviews']
  },
  {
    id: 102,
    title: 'Fall 2025 Application Deadline Session',
    event_type: 'webinar',
    event_date: '2024-12-01',
    attendees: 500,
    featured_image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800',
    highlights: ['Deadline calendar', 'Document checklist', 'Priority universities']
  },
  {
    id: 103,
    title: 'New Zealand Education Fair',
    event_type: 'expo',
    event_date: '2024-11-20',
    attendees: 300,
    featured_image: 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=800',
    highlights: ['8 universities participated', '50+ on-spot admits', 'Visa guidance']
  }
];

// Event type icons
const eventTypeIcons = {
  expo: Globe,
  fair: Globe,
  webinar: Video,
  workshop: BookOpen,
  seminar: Mic,
  open_day: Users,
  other: Calendar
};

// Event type colors
const eventTypeColors = {
  expo: 'bg-ux-violet/10 text-ux-violet border-ux-violet/30',
  fair: 'bg-ux-violet/10 text-ux-violet border-ux-violet/30',
  webinar: 'bg-ux-cyan/10 text-ux-cyan border-ux-cyan/30',
  workshop: 'bg-ux-coral/10 text-ux-coral border-ux-coral/30',
  seminar: 'bg-ux-mint/10 text-ux-mint border-ux-mint/30',
  open_day: 'bg-ux-amber/10 text-ux-amber border-ux-amber/30',
  other: 'bg-gray-500/10 text-gray-500 border-gray-500/30'
};

// Format date
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

// Check if event is upcoming
const isUpcoming = (dateStr) => {
  return new Date(dateStr) >= new Date();
};

export const EventsPage = () => {
  const { isDark } = useTheme();
  const [selectedType, setSelectedType] = useState('all');
  const [selectedMode, setSelectedMode] = useState('all');
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState(staticPastEvents);
  const [loading, setLoading] = useState(true);
  const [isUsingStaticData, setIsUsingStaticData] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setNewsletterLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail.trim(), source: 'events_page' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Subscribe failed');
      toast.success(data.message || "You're subscribed!");
      setNewsletterEmail('');
    } catch (err) {
      toast.error(err.message || 'Could not subscribe. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  // CMS Page Content (Phase D wiring)
  const { content: cmsContent } = usePageContent('events-list');
  const heroContent = getSection(cmsContent, 'hero', {
    badge: 'Events & Webinars',
    title: 'Learn, Connect,',
    title_highlight: 'Succeed',
    subtitle: 'Join our education fairs, webinars, and workshops. Meet university representatives, get expert guidance, and take the next step in your study abroad journey.',
    cta_primary_text: 'View Upcoming Events',
    cta_secondary_text: 'Get Event Alerts'
  });
  const pastHeader = getSection(cmsContent, 'past_header', {
    badge: 'Past Events',
    title: 'Event',
    title_highlight: 'Highlights',
    subtitle: 'Missed an event? Check out highlights from our previous sessions'
  });
  const emptyState = getSection(cmsContent, 'empty_state', {
    title: 'No events found for the selected filters.',
    subtitle: '',
    clear_label: 'Clear Filters'
  });
  const newsletterCta = getSection(cmsContent, 'newsletter_cta', {
    title: 'Never Miss an Event',
    subtitle: 'Subscribe to our event alerts and be the first to know about upcoming education fairs, webinars, and exclusive workshops.',
    email_placeholder: 'Enter your email',
    subscribe_label: 'Subscribe'
  });

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Fetch upcoming events
        const upcomingRes = await fetch(`${API_URL}/api/public/events?upcoming_only=true&limit=50`);
        if (upcomingRes.ok) {
          const data = await upcomingRes.json();
          if (data.events && data.events.length > 0) {
            setEvents(data.events);
            setIsUsingStaticData(false);
          } else {
            setEvents(staticEvents);
            setIsUsingStaticData(true);
          }
        } else {
          setEvents(staticEvents);
          setIsUsingStaticData(true);
        }

        // Fetch past events
        const pastRes = await fetch(`${API_URL}/api/public/events/past?limit=6`);
        if (pastRes.ok) {
          const data = await pastRes.json();
          if (data.events && data.events.length > 0) {
            setPastEvents(data.events);
          }
        }
      } catch (e) {
        console.error('Failed to fetch events:', e);
        setEvents(staticEvents);
        setIsUsingStaticData(true);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Filter upcoming events
  const upcomingEvents = events.filter(event => isUpcoming(event.event_date || event.date));
  const featuredEvents = upcomingEvents.filter(event => event.is_featured || event.isFeatured);
  
  const filteredEvents = upcomingEvents.filter(event => {
    const eventType = event.event_type || event.type;
    const eventMode = event.is_online ? 'online' : (event.mode || 'in-person');
    const typeMatch = selectedType === 'all' || eventType === selectedType;
    const modeMatch = selectedMode === 'all' || eventMode === selectedMode;
    return typeMatch && modeMatch;
  });

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'} transition-colors duration-500`} data-testid="events-page">
      {/* SEO Meta Tags */}
      <SEO 
        title="Study Abroad Events & Webinars - University Fairs, IELTS Workshops"
        description="Attend free study abroad events, university fairs, IELTS workshops, and visa webinars. Meet university representatives from USA, UK, Canada, Australia. Register now!"
        keywords="study abroad events, university fair India, education expo, IELTS workshop, study abroad webinar, USA university fair, UK education fair, Canada immigration seminar, study abroad workshop, overseas education events"
        canonical="https://unixperts.com/events"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "UniXperts Study Abroad Events",
          "description": "Upcoming study abroad events, university fairs, and webinars",
          "itemListElement": events.filter(e => new Date(e.date) >= new Date()).slice(0, 5).map((event, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Event",
              "name": event.title,
              "description": event.description,
              "startDate": event.date,
              "eventAttendanceMode": event.mode === 'online' 
                ? "https://schema.org/OnlineEventAttendanceMode" 
                : "https://schema.org/OfflineEventAttendanceMode",
              "location": event.mode === 'online' 
                ? { "@type": "VirtualLocation", "url": "https://unixperts.com/events" }
                : { "@type": "Place", "name": event.location },
              "organizer": {
                "@type": "Organization",
                "name": "UniXperts"
              },
              "image": event.image,
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "INR",
                "availability": "https://schema.org/InStock"
              }
            }
          }))
        }}
      />
      
      {/* Hero Section - Cinematic */}
      <section className="py-20 md:py-28 relative overflow-hidden min-h-[60vh] flex items-center">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/bf79016cb33766b48cd4d1a6ff5494fc240af259117d37106e38e1c1134290e3.png"
            alt="Education fair with university booths"
            className="w-full h-full object-cover"
          />
          {/* Cinematic gradient overlay */}
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-[#1a2830]/95 via-[#233842]/80 to-transparent' : 'bg-gradient-to-r from-ux-charcoal/95 via-ux-charcoal/80 to-ux-violet-dark/70'}`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Sparkles className="w-4 h-4 text-ux-amber" />
              <span className="text-sm font-medium text-white">{heroContent.badge}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white tracking-tight" data-testid="events-hero-title">
              {heroContent.title}
              <span className="gradient-text-aurora"> {heroContent.title_highlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed">
              {heroContent.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-ux-coral to-ux-amber text-white rounded-full px-8 py-6 text-lg font-bold hover:shadow-glow-coral hover:scale-105 transition-all"
                asChild
              >
                <a href="#upcoming-events">
                  {heroContent.cta_primary_text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button 
                size="lg"
                variant="hero-outline"
                className="rounded-full px-8 py-6 text-lg font-medium"
              >
                <Bell className="mr-2 h-5 w-5" />
                {heroContent.cta_secondary_text}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events */}
      {featuredEvents.length > 0 && (
        <section className="py-12 bg-gradient-to-r from-ux-violet via-ux-cyan to-ux-blue">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white font-display">Featured Events</h2>
              <Badge className="bg-white/20 text-white">{featuredEvents.length} Events</Badge>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.map((event) => {
                const eventType = event.event_type || event.type;
                const TypeIcon = eventTypeIcons[eventType] || Calendar;
                const eventDate = event.event_date || event.date;
                const registrationLink = event.registration_link;
                const maxAttendees = event.max_attendees;
                
                const CardWrapper = isUsingStaticData ? 'div' : Link;
                const cardProps = isUsingStaticData 
                  ? { className: `rounded-3xl p-6 hover:scale-[1.02] transition-all group block cursor-default ${isDark ? 'glass-panel' : 'bg-white shadow-lg border border-gray-100'}` }
                  : { to: `/events/${event.id}`, className: `rounded-3xl p-6 hover:scale-[1.02] transition-all group block ${isDark ? 'glass-panel' : 'bg-white shadow-lg border border-gray-100'}` };
                
                return (
                  <CardWrapper key={event.id} {...cardProps}>
                    <div className="flex items-start justify-between mb-3">
                      <Badge className="bg-ux-coral text-white border-none">
                        Featured
                      </Badge>
                      <TypeIcon className={`h-5 w-5 ${isDark ? 'text-white/70' : 'text-ux-charcoal/50'}`} />
                    </div>
                    <h3 className={`font-display font-bold text-lg mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{event.title}</h3>
                    <div className={`flex items-center gap-2 text-sm mb-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                      <Calendar className="h-4 w-4" />
                      {formatDate(eventDate)}
                    </div>
                    <div className={`flex items-center gap-2 text-sm mb-4 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                      <MapPin className="h-4 w-4" />
                      {event.location || (event.is_online ? 'Online' : 'TBA')}
                    </div>
                    <div className="flex items-center justify-between">
                      {maxAttendees && (
                        <span className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                          <Users className="h-3.5 w-3.5 inline mr-1" />{maxAttendees} seats
                        </span>
                      )}
                      {registrationLink ? (
                        <a 
                          href={registrationLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button size="sm" className="bg-ux-violet text-white hover:bg-ux-cyan rounded-full">
                            Register <ExternalLink className="ml-1 h-3 w-3" />
                          </Button>
                        </a>
                      ) : (
                        <Button size="sm" className="bg-ux-violet text-white hover:bg-ux-cyan rounded-full">
                          {isUsingStaticData ? 'Coming Soon' : 'View Details'}
                        </Button>
                      )}
                    </div>
                  </CardWrapper>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      <section id="upcoming-events" className={`py-16 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
            <div>
              <Badge className={`mb-4 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
                Upcoming Events
              </Badge>
              <h2 className={`font-display text-3xl md:text-4xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Don't Miss
                <span className="gradient-text-electric"> Out</span>
              </h2>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Filter className={`h-4 w-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`} />
                <select 
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className={`px-4 py-2 rounded-full border ${isDark ? 'bg-white/5 border-white/20 text-white' : 'bg-white border-gray-200 text-ux-charcoal'} focus:outline-none focus:ring-2 focus:ring-ux-cyan`}
                >
                  <option value="all">All Types</option>
                  <option value="expo">Education Fairs</option>
                  <option value="webinar">Webinars</option>
                  <option value="workshop">Workshops</option>
                </select>
              </div>
              <select 
                value={selectedMode}
                onChange={(e) => setSelectedMode(e.target.value)}
                className={`px-4 py-2 rounded-full border ${isDark ? 'bg-white/5 border-white/20 text-white' : 'bg-white border-gray-200 text-ux-charcoal'} focus:outline-none focus:ring-2 focus:ring-ux-cyan`}
              >
                <option value="all">All Modes</option>
                <option value="online">Online</option>
                <option value="in-person">In-Person</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {filteredEvents.map((event) => {
              const eventType = event.event_type || event.type;
              const TypeIcon = eventTypeIcons[eventType] || Calendar;
              const eventDate = event.event_date || event.date;
              const eventTime = event.event_time || event.time;
              const tags = event.tags || [];
              const registrationLink = event.registration_link;
              const maxAttendees = event.max_attendees;
              
              const CardWrapper = isUsingStaticData ? 'div' : Link;
              const cardProps = isUsingStaticData 
                ? { className: "glass-panel rounded-3xl overflow-hidden hover:scale-[1.01] transition-all group block cursor-default" }
                : { to: `/events/${event.id}`, className: "glass-panel rounded-3xl overflow-hidden hover:scale-[1.01] transition-all group block" };
              
              return (
                <CardWrapper 
                  key={event.id}
                  {...cardProps}
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="grid md:grid-cols-5">
                    {/* Image */}
                    <div className="md:col-span-2 relative h-48 md:h-full">
                      <img 
                        src={event.featured_image || event.image || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800'}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40" />
                      <div className="absolute top-4 left-4">
                        <Badge className={eventTypeColors[eventType] || eventTypeColors.other}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {eventType.charAt(0).toUpperCase() + eventType.slice(1).replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="md:col-span-3 p-6">
                      <h3 className={`font-display font-bold text-xl mb-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {event.title}
                      </h3>
                      <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-white/60' : 'text-ux-charcoal/70'}`}>
                        {event.description}
                      </p>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-ux-cyan" />
                          <span className={isDark ? 'text-white' : 'text-ux-charcoal'}>{formatDate(eventDate)}</span>
                        </div>
                        {eventTime && (
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-ux-violet" />
                            <span className={isDark ? 'text-white' : 'text-ux-charcoal'}>{eventTime}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-ux-coral" />
                          <span className={isDark ? 'text-white' : 'text-ux-charcoal'}>{event.location || (event.is_online ? 'Online' : 'TBA')}</span>
                        </div>
                      </div>

                      {/* Tags */}
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {tags.map((tag) => (
                            <Badge key={tag} variant="outline" className={`text-xs ${isDark ? 'border-white/20 text-white/70' : ''}`}>
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Event Info */}
                      {maxAttendees && (
                        <div className={`flex items-center gap-2 text-sm mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                          <Users className="h-4 w-4" />
                          <span>Limited to {maxAttendees} seats</span>
                        </div>
                      )}

                      {registrationLink ? (
                        <a 
                          href={registrationLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="block w-full"
                        >
                          <Button className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-full">
                            Register Now
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </Button>
                        </a>
                      ) : (
                        <Button className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-full">
                          {isUsingStaticData ? 'Coming Soon' : 'View Details'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardWrapper>
              );
            })}
          </div>

          {filteredEvents.length === 0 && (
            <div className="text-center py-12">
              <p className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>{emptyState.title}</p>
              <Button 
                variant="link" 
                onClick={() => { setSelectedType('all'); setSelectedMode('all'); }}
                className="text-ux-cyan"
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Past Events */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className={`mb-4 ${isDark ? 'bg-white/10 text-white' : 'bg-ux-charcoal/10 text-ux-charcoal'}`}>
              {pastHeader.badge}
            </Badge>
            <h2 className={`font-display text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="past-events-header">
              {pastHeader.title}
              <span className="gradient-text-brand"> {pastHeader.title_highlight}</span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
              {pastHeader.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pastEvents.map((event) => {
              const eventDate = event.event_date || event.date;
              const eventImage = event.featured_image || event.image || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800';
              const highlights = event.highlights || [];
              return (
                <div key={event.id} className="glass-panel rounded-3xl overflow-hidden hover:scale-[1.02] transition-all group">
                  <div className="relative h-48">
                    <img 
                      src={eventImage}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-display font-bold">{event.title}</p>
                      <p className="text-sm text-white/80">{formatDate(eventDate)}</p>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="h-4 w-4 text-ux-cyan" />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{event.attendees || event.registrations || 0} Attendees</span>
                    </div>
                    {highlights.length > 0 && (
                      <div className="space-y-2">
                        {highlights.map((highlight, index) => (
                          <div key={index} className={`flex items-center gap-2 text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                            <ChevronRight className="h-3 w-3 text-ux-cyan" />
                            {highlight}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ux-blue via-ux-violet to-ux-cyan" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
            <Bell className="h-8 w-8 text-white" />
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-6 text-white" data-testid="events-newsletter-title">
            {newsletterCta.title}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {newsletterCta.subtitle}
          </p>
          <form
            onSubmit={handleNewsletterSubmit}
            className="flex flex-col sm:flex-row gap-3 sm:items-stretch justify-center max-w-md mx-auto"
            data-testid="newsletter-subscribe-form"
          >
            <input 
              type="email"
              required
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder={newsletterCta.email_placeholder}
              disabled={newsletterLoading}
              className="flex-1 h-14 px-6 rounded-full bg-white/10 border border-white/25 text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-white/60 focus:border-white/60 backdrop-blur-sm disabled:opacity-60"
              data-testid="newsletter-email-input"
            />
            <Button
              type="submit"
              disabled={newsletterLoading}
              className="h-14 bg-white text-ux-violet hover:bg-ux-green hover:text-ux-charcoal rounded-full px-8 font-bold transition-all disabled:opacity-70 disabled:hover:bg-white disabled:hover:text-ux-violet"
              data-testid="newsletter-subscribe-btn"
            >
              {newsletterLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Subscribing...
                </>
              ) : (
                newsletterCta.subscribe_label
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
